import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { formsApi, locationsApi } from '../../services/api';
import {
    Download,
    FileText,
    AlertCircle,
    Filter,
    BarChart3,
    RefreshCw,
    Calendar
} from 'lucide-react';
import LocationReportGroup from '../components/reports/LocationReportGroup';
import ReportExporter from '../components/reports/ReportExporter';

const ReportsPage = () => {
    const { selectedCorporate } = useOutletContext();
    const [availableForms, setAvailableForms] = useState([]);
    const [availableLocations, setAvailableLocations] = useState([]);
    const [selectedFormId, setSelectedFormId] = useState(null);
    const [selectedLocationId, setSelectedLocationId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [exportLoading, setExportLoading] = useState(false);
    const [datePreset, setDatePreset] = useState('lastWeek');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    const getDateRange = () => {
        const now = new Date();
        if (datePreset === 'lastWeek') {
            const start = new Date(now);
            start.setDate(now.getDate() - 7);
            return { startDate: start.toISOString(), endDate: now.toISOString() };
        } else if (datePreset === 'lastMonth') {
            const start = new Date(now);
            start.setMonth(now.getMonth() - 1);
            return { startDate: start.toISOString(), endDate: now.toISOString() };
        } else if (datePreset === 'custom') {
            return {
                startDate: customStartDate ? new Date(customStartDate).toISOString() : null,
                endDate: customEndDate ? new Date(customEndDate).toISOString() : null
            };
        }
        // allTime
        return { startDate: null, endDate: null };
    };

    useEffect(() => {
        loadAvailableForms();
        loadAvailableLocations();
    }, [selectedCorporate]);

    const loadAvailableForms = async () => {
        if (!selectedCorporate?.id) return;
        try {
            const data = await formsApi.getTemplates(selectedCorporate.id);
            setAvailableForms(data || []);
            if (data?.length > 0) {
                setSelectedFormId(data[0].id);
            }
        } catch (err) {
            console.error('Form yükleme hatası:', err);
            setError('Formlar yüklenemedi');
        }
    };

    const loadAvailableLocations = async () => {
        if (!selectedCorporate?.id) return;
        try {
            const data = await locationsApi.getAll(selectedCorporate.id);
            setAvailableLocations(data || []);
        } catch (err) {
            console.error('Lokasyon yükleme hatası:', err);
        }
    };

    const generateReport = async () => {
        if (!selectedFormId) {
            setError('Lütfen bir form seçiniz');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            // Fetch submission report with all details including FormTemplate and answers
            const { startDate, endDate } = getDateRange();
            const reportSubmissions = await formsApi.getSubmissionReport(
                selectedCorporate?.id,
                selectedLocationId || null,
                selectedFormId,
                startDate,
                endDate
            );

            // Get formTemplate from first submission (all submissions have same template)
            const formTemplate = reportSubmissions?.[0]?.formTemplate;

            if (!formTemplate) {
                setError('Gösterilecek veri bulunamadı');
                setLoading(false);
                return;
            }

            // Process data for report
            const processedData = processReportData(
                reportSubmissions || [],
                formTemplate,
                selectedLocationId
            );

            setReportData(processedData);
        } catch (err) {
            console.error('Rapor oluşturma hatası:', err);
            setError('Rapor oluşturulamadı: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const processReportData = (submissions, formTemplate, locationFilter) => {
        const questions = formTemplate.questions || [];

        // Group submissions by location
        const submissionsByLocation = {};
        submissions.forEach(submission => {
            const locId = submission.locationId || 'unknown';
            if (!submissionsByLocation[locId]) {
                submissionsByLocation[locId] = [];
            }
            submissionsByLocation[locId].push(submission);
        });

        // Process each question
        const processedQuestions = questions.map(question => {
            const questionAnswers = {
                questionId: question.id,
                questionText: question.questionText,
                questionType: question.questionType, // 1: Text, 2: Star, 3: MultiChoice
                options: question.options || [],
                answersByLocation: {}
            };

            // Group answers by location
            Object.entries(submissionsByLocation).forEach(([locId, submissionsList]) => {
                const locationAnswers = [];
                submissionsList.forEach(submission => {
                    // Find answer by FormQuestionId (from FormAnswerDTO)
                    const answer = submission.answers?.find(a => a.formQuestionId === question.id);
                    if (answer) {
                        locationAnswers.push(answer);
                    }
                });

                questionAnswers.answersByLocation[locId] = {
                    locationId: locId,
                    locationName: submissionsList[0]?.locationName || 'Bilinmeyen Lokasyon',
                    answers: locationAnswers,
                    stats: calculateAnswerStats(locationAnswers, question)
                };
            });

            return questionAnswers;
        });

        return {
            formId: selectedFormId,
            formName: formTemplate.title,
            totalSubmissions: submissions.length,
            questions: processedQuestions,
            allSubmissions: submissions,
            locations: availableLocations
        };
    };

    const calculateAnswerStats = (answers, question) => {
        const stats = {
            totalAnswers: answers.length,
            multiChoice: {},
            starRating: {},
            freeText: {}
        };

        if (!question || !answers) {
            return stats;
        }

        if (question.questionType === 3) {
            // Multi-choice - initialize all options with 0, then count selections
            if (question.options && question.options.length > 0) {
                question.options.forEach(option => {
                    stats.multiChoice[option.optionText] = 0;
                });
            }
            // Count selected options from answers
            answers.forEach(a => {
                if (a.selectedOptionText) {
                    stats.multiChoice[a.selectedOptionText] = (stats.multiChoice[a.selectedOptionText] || 0) + 1;
                }
            });
        } else if (question.questionType === 2) {
            // Star rating
            for (let i = 1; i <= 5; i++) {
                const count = answers.filter(a => a.ratingValue === i).length;
                if (count > 0) {
                    stats.starRating[i] = count;
                }
            }
        } else if (question.questionType === 1) {
            // Free text - group by similar answers
            const textGroups = {};
            answers.forEach(a => {
                const text = (a.answerText || '').toLowerCase().trim();
                if (text) {
                    textGroups[text] = (textGroups[text] || 0) + 1;
                }
            });
            stats.freeText = textGroups;
        }

        return stats;
    };

    const handleExport = async (format) => {
        setExportLoading(true);
        try {
            const exporter = new ReportExporter();
            if (format === 'pdf') {
                await exporter.exportToPDF(reportData);
            } else if (format === 'xlsx') {
                await exporter.exportToXLSX(reportData);
            }
        } catch (err) {
            console.error('Export hatası:', err);
            console.error('Export error full:', err.toString());
            alert('Dışa aktarma hatası: ' + (err.message || err.toString()));
        } finally {
            setExportLoading(false);
        }
    };

    if (!selectedCorporate?.id) {
        return (
            <div className="p-6 bg-white rounded-lg border border-yellow-200 text-yellow-800">
                <AlertCircle className="w-5 h-5 inline mr-2" />
                Lütfen bir kurum seçiniz
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                        <BarChart3 className="w-8 h-8 text-blue-600" />
                        Raporlar
                    </h1>
                    <p className="text-gray-600 mt-1">Form yanıtlarını analiz edin ve dışa aktarın</p>
                </div>
            </div>

            {/* Filters Card */}
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Filter className="w-5 h-5 text-blue-600" />
                    Filtreler
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Date Range */}
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Calendar className="inline w-4 h-4 mr-1" />
                            Tarih Aralığı
                        </label>
                        <div className="flex flex-wrap gap-2 mb-3">
                            {[
                                { value: 'lastWeek', label: 'Son 1 Hafta' },
                                { value: 'lastMonth', label: 'Son 1 Ay' },
                                { value: 'allTime', label: 'Tüm Zamanlar' },
                                { value: 'custom', label: 'Özel Aralık' }
                            ].map(preset => (
                                <button
                                    key={preset.value}
                                    type="button"
                                    onClick={() => { setDatePreset(preset.value); setReportData(null); }}
                                    className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${datePreset === preset.value
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                        }`}
                                >
                                    {preset.label}
                                </button>
                            ))}
                        </div>
                        {datePreset === 'custom' && (
                            <div className="flex flex-wrap gap-3 items-center">
                                <div className="flex items-center gap-2">
                                    <label className="text-sm text-gray-600 whitespace-nowrap">Başlangıç:</label>
                                    <input
                                        type="date"
                                        value={customStartDate}
                                        max={new Date().toISOString().split('T')[0]}
                                        onChange={(e) => { setCustomStartDate(e.target.value); setReportData(null); }}
                                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    <label className="text-sm text-gray-600 whitespace-nowrap">Bitiş:</label>
                                    <input
                                        type="date"
                                        value={customEndDate}
                                        max={new Date().toISOString().split('T')[0]}
                                        onChange={(e) => { setCustomEndDate(e.target.value); setReportData(null); }}
                                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Form Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Form *
                        </label>
                        <select
                            value={selectedFormId || ''}
                            onChange={(e) => {
                                setSelectedFormId(e.target.value || null);
                                setReportData(null);
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                        >
                            <option value="">Form Seçiniz</option>
                            {availableForms.map(form => (
                                <option key={form.id} value={form.id}>
                                    {form.title}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Location Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Lokasyon
                        </label>
                        <select
                            value={selectedLocationId || ''}
                            onChange={(e) => {
                                setSelectedLocationId(e.target.value || null);
                                setReportData(null);
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Tüm Lokasyonlar</option>
                            {availableLocations.map(location => (
                                <option key={location.id} value={location.id}>
                                    {location.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Generate Report Button */}
                <div className="mt-4 flex gap-2">
                    <button
                        onClick={generateReport}
                        disabled={loading || !selectedFormId}
                        className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                        {loading ? (
                            <>
                                <RefreshCw className="w-4 h-4 animate-spin" />
                                Yükleniyor...
                            </>
                        ) : (
                            <>
                                <FileText className="w-4 h-4" />
                                Raporu Oluştur
                            </>
                        )}
                    </button>
                </div>

                {error && (
                    <div className="mt-4 p-4 bg-red-100 text-red-800 rounded-lg flex items-center gap-2">
                        <AlertCircle className="w-5 h-5" />
                        {error}
                    </div>
                )}
            </div>

            {/* Report Data */}
            {reportData && (
                <>
                    {/* Summary Section */}
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg shadow-md p-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <p className="text-blue-100 text-sm">Form</p>
                                <p className="text-xl font-bold">{reportData.formName}</p>
                            </div>
                            <div>
                                <p className="text-blue-100 text-sm">Toplam Yanıt</p>
                                <p className="text-2xl font-bold">{reportData.totalSubmissions}</p>
                            </div>
                            <div>
                                <p className="text-blue-100 text-sm">Soru Sayısı</p>
                                <p className="text-2xl font-bold">{reportData.questions.length}</p>
                            </div>
                        </div>
                    </div>

                    {/* Export Buttons */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => handleExport('pdf')}
                            disabled={exportLoading}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 transition-colors"
                        >
                            <Download className="w-4 h-4" />
                            {exportLoading ? 'Dışa Aktarılıyor...' : 'PDF İndir'}
                        </button>
                        <button
                            onClick={() => handleExport('xlsx')}
                            disabled={exportLoading}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors"
                        >
                            <Download className="w-4 h-4" />
                            {exportLoading ? 'Dışa Aktarılıyor...' : 'Excel İndir'}
                        </button>
                    </div>

                    {/* Questions Report */}
                    <div className="space-y-6">
                        {reportData.questions.map((question, index) => (
                            <LocationReportGroup
                                key={question.questionId}
                                question={question}
                                questionNumber={index + 1}
                            />
                        ))}
                    </div>
                </>
            )}

            {/* No Data Message */}
            {!loading && !reportData && !error && (
                <div className="bg-gray-50 rounded-lg border border-dashed border-gray-300 p-12 text-center">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600">Raporu oluşturmak için bir form seçin ve "Raporu Oluştur" butonuna tıklayın</p>
                </div>
            )}
        </div>
    );
};

export default ReportsPage;
